import React, { useState, useMemo, useRef } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useProfileAbility } from 'context/profile';
import { toast } from 'components/Shared/Toast';
import TenantMembersSelect from './MemberSelection';
import { getMongoObjectId, findSelectOption } from 'utils';
import { fetcher } from 'api/client';
import { fetchWorkflow, updateWorkflow } from 'api/actions/workflows';
import bootbox from 'components/Modals/Bootbox';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import produce from 'immer';
import { FormGroup, Collapse, CardGroup, Card, InputGroup, InputGroupAddon, Input, Button } from 'reactstrap';
import { reactSelectStyles } from 'components/Shared/ReactSelectStyles'

function Step({ step: { _id, type, user, group, order }, handleTypeChange, handleRemoveStep, handleStepGroupChange, handleMemberChange, canModify }) {

    const { t } = useTranslation();

    const [isOpen, setisopen] = useState(false);

    const toggle = () => setisopen(!isOpen);

    const stepOptions = useMemo(() => [
        { value: 'delegate', label: t('workflow.type.delegate'), showMembers: true },
        { value: 'group-delegate', label: t('workflow.type.group-delegate'), showGroups: true },
        { value: 'approval', label: t('workflow.type.approval'), showMembers: true },
        { value: 'group-approval', label: t('workflow.type.group-approval'), showGroups: true },
        // { value: "finalize", label: t('workflow.type.finalize') }, // TODO: NOT IMPLEMENTED
        { value: 'sign', label: t('workflow.type.sign') },
        { value: 'delegate-and-sign', label: t('workflow.type.delegate-and-sign'), showMembers: true }
    ], [t]);

    const [currentOption, setcurrentOption] = useState(findSelectOption(stepOptions, type))

    const doHandleStepRemove = async () => {
        if (await bootbox.confirm(t('workflows.confirm_delete_step')))
            handleRemoveStep(_id);
    }

    const doHandleTypeChange = ({ value }) => {
        const option = findSelectOption(stepOptions, value);
        setcurrentOption(option);
        handleTypeChange(option, _id);
    }

    const doHandleStepGroupChange = ({ value }) => handleStepGroupChange(value, _id)

    const doHandleStepMemberChange = ({ value }) => handleMemberChange(value, _id);

    return (
        <div className="mb-0">
            <InputGroup onClick={toggle} className="pointer">
                <InputGroupAddon addonType="prepend" >{String(order + 1)}</InputGroupAddon>
                <Input className="pointer bg-white" disabled value={t(`workflow.type.${type}`)} />
                <Button onClick={doHandleStepRemove} disabled={!canModify} color="light"><i className="fas fa-times"></i></Button>
            </InputGroup>
            {
                currentOption &&
                <Collapse isOpen={isOpen} className="p-3 bg-light">
                    <FormGroup>
                        <label>{t('workflow.new.type')}:</label>
                        <Select
                            isDisabled={!canModify}
                            onChange={doHandleTypeChange}
                            styles={reactSelectStyles} 
                            value={currentOption}
                            options={stepOptions}
                            name="type"
                            className="m-b"
                        />
                    </FormGroup>
                    {(currentOption.showGroups || currentOption.showMembers) &&
                        <FormGroup>
                            <label>{t('workflow.new.receiver')}</label>
                            {currentOption.showGroups ?
                                <TenantMembersSelect
                                    isDisabled={!canModify}
                                    name={`GroupSelect${_id}`}
                                    key={`GroupSelect${_id}`}
                                    val={group}
                                    onChange={doHandleStepGroupChange}
                                />
                                :
                                <TenantMembersSelect
                                    isDisabled={!canModify}
                                    name={`MemberSelect${_id}`}
                                    key={`MemberSelect${_id}`}
                                    showMembers={true}
                                    val={user}
                                    onChange={doHandleStepMemberChange}
                                />
                            }
                        </FormGroup>
                    }
                </Collapse>
            }
        </div>)
}

export default function WorkflowStepView({ workflowId, setCurrentworkflow, currentWorkflow }) {

    const { t } = useTranslation();

    const queryClient = useQueryClient();

    const { data: workflow = {}, isSuccess } = useQuery(['workflow', workflowId], () => fetcher(fetchWorkflow(workflowId)), {
        enabled: !!workflowId,
        refetchOnWindowFocus: false
    });

    const { mutate: mutateUpdateWorkflow } = useMutation((workflow) => fetcher(updateWorkflow(workflow),), {
        onSuccess: () => {
            setCurrentworkflow({ ...currentWorkflow, hasChanges: false });
            toast.success(t('success'));
        },
        onError: (error, variables) => {
            queryClient.invalidateQueries(['workflow', variables?._id])
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: (variables) => {
            queryClient.invalidateQueries('workflows-fetch')
            queryClient.invalidateQueries(['workflow', variables?._id])
        },
    });

    const profileAbility = useProfileAbility();
    const canModify = profileAbility.can('modify', { ...workflow, __type: 'Workflow' });
    const nameInputRef = useRef(null);

    const handleSave = () => {
        if (canModify && currentWorkflow.hasChanges) mutateUpdateWorkflow({ ...workflow });
    }

    const setQuery = (payload) => {
        if (!canModify) return;
        setCurrentworkflow({ ...currentWorkflow, hasChanges: true });
        queryClient.setQueryData(['workflow', workflow._id], payload);
    }

    const handleAddStep = () => setQuery(produce(workflow, draft => {
        draft.steps.push({ type: 'sign', order: workflow.steps.length, _id: getMongoObjectId() })
    }));

    const updateName = () => setQuery(produce(workflow, draft => {
        draft.name = nameInputRef.current.value;
    }));

    const handleRemoveStep = (stepId) => setQuery(produce(workflow, draft => {
        draft.steps = draft.steps.filter((step) => step._id !== stepId);
    }));

    const handleGroupChange = (opts) => setQuery(produce(workflow, draft => {
        draft.groups = opts?.map(g => g.value) || [];
    }));

    const handleStepGroupChange = (value, stepId) => setQuery(produce(workflow, draft => {
        const index = draft.steps.findIndex(m => m._id === stepId);
        if (index !== -1) {
            draft.steps[index].user = undefined;
            draft.steps[index].group = value;
        }
    }));

    const handleMemberChange = (value, stepId) => setQuery(produce(workflow, draft => {
        const index = draft.steps.findIndex(m => m._id === stepId);
        if (index !== -1) {
            draft.steps[index].user = value;
            draft.steps[index].group = undefined;
        }
    }));

    const handleTypeChange = (option, stepId) => setQuery(produce(workflow, draft => {
        const index = draft.steps.findIndex(m => m._id === stepId);
        if (index !== -1) {
            draft.steps[index].type = option.value;
            draft.steps[index].group = option.showGroups ? draft.steps[index].group : undefined;
            draft.steps[index].user = option.showMembers ? draft.steps[index].user : undefined;
        }
    }));

    return (
        <div className="detail-content position-relative p-3 h-100 d-flex flex-column overflow-hidden">

            <p className="text-justify">
                {t('workflow.new.description')}
            </p>

            <FormGroup>
                <label>{t('workflow.new.name')}:</label>
                <input
                    disabled={!canModify}
                    key={`name${workflow._id}`}
                    type="text"
                    className="form-control"
                    id="workflowname"
                    ref={nameInputRef}
                    placeholder={t('workflow.new.name_placeholder')}
                    value={workflow.name}
                    onChange={updateName}
                />
            </FormGroup>

            <FormGroup>
                <label>{t('workflow.new.group')}:</label>
                <TenantMembersSelect
                    isDisabled={!canModify}
                    val={workflow.groups}
                    isMulti
                    onChange={handleGroupChange}
                />
            </FormGroup>

            <FormGroup >
                <label>{t('workflow.new.stages')}:</label>
            </FormGroup>
            <div className="steps-listing border overflow-auto h-100">
                <div className="workflow-steps-container">
                    {isSuccess && workflow.steps?.map((step) => (
                        <Step
                            step={step}
                            key={step._id}
                            canModify={canModify}
                            handleTypeChange={handleTypeChange}
                            handleRemoveStep={handleRemoveStep}
                            handleStepGroupChange={handleStepGroupChange}
                            handleMemberChange={handleMemberChange}
                        />
                    ))}
                </div>
            </div>

            {/* footer */}
            <div className={clsx('mt-3')}>
                <Button block color="secondary" disabled={!canModify} onClick={handleAddStep}>
                    <i className="fas fa-plus"></i> {t('workflow.new.add_stage')}
                </Button>
                <Button block color="primary" disabled={!canModify} onClick={handleSave}>
                    <i className={clsx('fas fa-save', currentWorkflow.hasChanges && 'grow')}></i> {t('workflow.new.save')}
                </Button>
            </div>
        </div>
    )
}